import { cloneElement, isValidElement, useEffect, useMemo, useState } from 'react';
import { RouteObject } from 'react-router';

// =====================================================================================
// Live money / date formatting.
//
// The company's saved settings (currency, decimals, date & time format, time zone) are loaded
// from the server in the BACKGROUND at start-up — the app never waits for them — and can change
// live (SignalR "settings_changed"). Amounts and dates are formatted while a screen renders, and the
// routed screens are static elements React does not render again by itself, so a screen drawn before
// the settings arrived would keep the fallback formatting.
//
// `notifyIfFormatChanged` fires COMPANY_FORMAT_CHANGED_EVENT only when what prints really changed,
// and `useLiveFormatRoutes` answers it with FRESH copies of the route elements: every open screen
// renders again straight away with the saved formatting. A re-render, not a remount — a cart being
// rung up, a half-typed form, loaded data and the scroll position all stay, and nothing reloads.
// =====================================================================================

export const COMPANY_FORMAT_CHANGED_EVENT = 'companyFormatChanged';

/** The fields of sessionStorage.companySettings that change how amounts and dates print. */
const FORMAT_FIELDS = [
  'currencyCode',
  'currencySymbol',
  'currencySymbolPosition',
  'decimalPlaces',
  'decimalSeparator',
  'thousandSeparator',
  'showCurrencySymbol',
  'showThousandSeparator',
  'dateFormat',
  'timeFormat',
  'dateTimeFormat',
  'timeZoneId',
  'timeZoneOffset',
];

/** A comparable fingerprint of the formatting part of this tab's company settings. */
export function companyFormatSignature(): string {
  try {
    const raw = sessionStorage.getItem('companySettings');
    if (!raw) return '';
    const s = JSON.parse(raw) as Record<string, unknown>;
    return JSON.stringify(FORMAT_FIELDS.map((f) => (s ? s[f] ?? null : null)));
  } catch {
    return '';
  }
}

/** Fire COMPANY_FORMAT_CHANGED_EVENT when the formatting differs from `before`. */
export function notifyIfFormatChanged(before: string): void {
  if (companyFormatSignature() === before) return;
  try {
    window.dispatchEvent(new Event(COMPANY_FORMAT_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

/** The same routes with fresh copies of their elements (same components, same props). */
function withFreshElements(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => ({
    ...route,
    element: isValidElement(route.element) ? cloneElement(route.element) : route.element,
    children: route.children ? withFreshElements(route.children) : route.children,
  }));
}

/**
 * The routes for `useRoutes`: returned as they are until the money / date formatting changes, then
 * with fresh element copies, so React renders every open screen again (its state kept) and amounts
 * and dates come out in the company's saved format at once.
 */
export function useLiveFormatRoutes(routes: RouteObject[]): RouteObject[] {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const onFormatChanged = () => setVersion((v) => v + 1);
    window.addEventListener(COMPANY_FORMAT_CHANGED_EVENT, onFormatChanged);
    return () => window.removeEventListener(COMPANY_FORMAT_CHANGED_EVENT, onFormatChanged);
  }, []);

  return useMemo(() => (version === 0 ? routes : withFreshElements(routes)), [routes, version]);
}
